import { motion } from 'motion/react';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@music-vine/cadence/ui';
import { Carousel, CarouselContent, CarouselItem } from '@music-vine/cadence/ui';
import { FadeAway } from '@music-vine/cadence/ui';
import { useCarouselDraggable } from '@/hooks/ui/useCarouselDraggable';
import type { CarouselApi } from '@music-vine/cadence/ui';
import { trackZoneSubFilterClick } from '../utils/events';
import { useRouter } from 'next/router';
interface ZoneSection {
  id: string;
  name: string;
  slug: string;
}
interface ZonesSubfilterProps {
  activeSection?: string;
  isVisible?: boolean;
  onSectionChange: (sectionId: string) => void;
  scrollToGrid?: () => void;
  sections: ZoneSection[];
}
const ZonesSubfilterComponent: React.FC<ZonesSubfilterProps> = ({
  sections,
  activeSection,
  onSectionChange,
  isVisible = true,
  scrollToGrid
}) => {
  const {
    isDraggable,
    containerRef
  } = useCarouselDraggable();
  const router = useRouter();

  // Add state for carousel scroll position (for fade indicators)
  const [scrollState, setScrollState] = useState<{
    canScrollNext: boolean;
    canScrollPrev: boolean;
  }>({
    canScrollNext: false,
    canScrollPrev: false
  });

  // Add API ref for carousel
  const [api, setApi] = useState<CarouselApi>();
  useEffect(() => {
    if (!api) {
      return;
    }
    const onSelect = () => {
      requestAnimationFrame(() => {
        const canScrollNext = api.canScrollNext();
        const canScrollPrev = api.canScrollPrev();
        setScrollState({
          canScrollNext,
          canScrollPrev
        });
      });
    };

    // Check initial scroll state
    onSelect();

    // Listen for changes
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('resize', onSelect);
    api.on('scroll', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('resize', onSelect);
      api.off('scroll', onSelect);
    };
  }, [api]);
  const handleSectionClick = (sectionId: string) => {
    const newSection = activeSection === sectionId ? '' : sectionId;
    onSectionChange(newSection);

    // Scroll to grid with a small delay to allow for state updates
    if (scrollToGrid) {
      setTimeout(() => {
        scrollToGrid();
      }, 100);
    }
    trackZoneSubFilterClick(router.asPath, newSection);
  };
  const getButtonClassName = (isSelected: boolean) => {
    return clsx(
    // Base
    'shrink-0 rounded-[10px] border border-solid px-3 py-2 text-sm transition-colors duration-200 focus-visible:ring-inset focus-visible:ring-offset-0',
    // Selected
    isSelected && ['border-pink-600 bg-pink-50 text-pink-600', 'dark:border-white dark:bg-gray-800', '[@media(hover:hover)]:hover:border-pink-500! [@media(hover:hover)]:hover:bg-pink-100! [@media(hover:hover)]:hover:text-pink-500!', 'dark:[@media(hover:hover)]:hover:border-white! dark:[@media(hover:hover)]:hover:bg-gray-800! dark:[@media(hover:hover)]:hover:text-white!'],
    // Unselected
    !isSelected && ['border-gray-150 bg-white [@media(hover:hover)]:hover:border-gray-150 [@media(hover:hover)]:hover:bg-gray-50', 'dark:border-gray-800! dark:bg-gray-950 dark:text-white dark:[@media(hover:hover)]:hover:bg-gray-900!']);
  };
  if (sections.length === 0) {
    return null;
  }
  return <motion.div animate={{
    opacity: isVisible ? 1 : 0,
    scaleY: isVisible ? 1 : 0
  }} className="bg-white dark:bg-gray-950" initial={false} style={{
    transformOrigin: 'top'
  }} transition={{
    duration: 0.25,
    ease: [0.33, 1, 0.68, 1]
  }} data-sentry-element="motion.div" data-sentry-component="ZonesSubfilterComponent" data-sentry-source-file="zonesSubfilter.tsx">
      <div className="pr-0 pl-container md:px-container">
        <Carousel className="w-full" opts={{
        align: 'start',
        dragFree: true,
        active: isDraggable
      }} setApi={setApi} data-sentry-element="Carousel" data-sentry-source-file="zonesSubfilter.tsx">
          <CarouselContent className="-ml-3 py-2" ref={containerRef} data-sentry-element="CarouselContent" data-sentry-source-file="zonesSubfilter.tsx">
            {sections.map(section => {
            const isSelected = activeSection === section.id;
            return <CarouselItem className="basis-auto pl-3 last-of-type:mr-container md:last-of-type:mr-0" key={section.id}>
                  <Button className={getButtonClassName(isSelected)} onClick={() => handleSectionClick(section.id)} variant="subtle">
                    {section.name}
                  </Button>
                </CarouselItem>;
          })}
          </CarouselContent>

          <FadeAway className="z-0 w-6 via-white dark:via-gray-950" direction="right" show={scrollState.canScrollNext} data-sentry-element="FadeAway" data-sentry-source-file="zonesSubfilter.tsx" />
          <FadeAway className="z-0 w-6 via-white dark:via-gray-950" direction="left" show={scrollState.canScrollPrev} data-sentry-element="FadeAway" data-sentry-source-file="zonesSubfilter.tsx" />
        </Carousel>
      </div>
    </motion.div>;
};

// Memoize the component to prevent unnecessary re-renders
export const ZonesSubfilter = React.memo(ZonesSubfilterComponent);